import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { capitalize } from 'lodash';
import moment from 'moment';

import { useStaffPracticeAdmin } from 'lib/hooks/queries/practice/useStaffPracticeAdmin';

import AgTablePage from 'components/shared/AgTablePage/AgTablePage';
import Allowed from 'components/shared/Permissions/Allowed';
import Popover from 'components/shared/Popovers/Popover/Popover';

import ColorCircle from './lib/ColorCircle';
import HeaderActions from './lib/HeaderActions';
import PracticeStaffModals from './lib/PracticeStaffModals';
import { DEFAULT_FILTERS } from './lib/defaultFilters';
import { copyToClipboard, navigateToVistProfile } from './lib/utility';

export default function AdminStaff() {
  const { id } = useParams();
  const [deletePracticeStaffModal, setDeletePracticeStaffModal] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [memberToSupervisor, setMemberToSupervisor] = useState();
  const [show, setShow] = useState({
    newMember: false,
    staffEdit: false,
    supervisorSettings: false,
    confirmImpersonate: false
  });
  const [isLoading, setIsLoading] = useState(false);

  const submitStaffRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      if (submitStaffRef.current?.isLoading !== isLoading) {
        setIsLoading(submitStaffRef.current?.isLoading);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isLoading]);

  const Actions = ({ data }) => {
    const actionOptions = [
      {
        label: 'Impersonate',
        icon: 'mask-mono',
        onClick: () => {
          setSelectedStaff(data);
          setShow({ ...show, confirmImpersonate: true });
        },
        permissions: {
          requiredPermissions: 'impersonate.create'
        }
      },
      {
        label: 'Visit profile',
        icon: 'eye-mono',
        onClick: () => {
          navigate(`/admin/practitioners/${data.id}`);
        }
      },
      {
        label: 'Edit',
        icon: 'edit-2-mono',
        onClick: () => {
          setSelectedStaff(data);
          setShow({ ...show, staffEdit: true });
        },
        permissions: {
          requiredPermissions: 'member.update'
        }
      },
      {
        label: 'Make supervisor',
        icon: 'user-octagon-mono',
        onClick: () => {
          setMemberToSupervisor(data);
          setShow({ ...show, supervisorSettings: true });
        }
      },
      {
        label: 'Delete',
        icon: 'trash-mono',
        onClick: () => {
          setSelectedStaff(data);
          setDeletePracticeStaffModal(true);
        },
        permissions: {
          requiredPermissions: 'member.delete'
        }
      }
    ];

    return (
      <Popover
        isFixed
        position="left"
        isBorder={false}
        options={actionOptions}
        className="flex h-full flex-col justify-center"
      />
    );
  };

  const cols = [
    {
      field: 'id',
      headerName: 'ID',
      maxWidth: 150,
      cellRenderer: (params) => (
        <div
          onClick={() => navigateToVistProfile(navigate, params.value)}
          style={{ cursor: 'pointer' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'fullName',
      headerName: 'Name',
      cellRenderer: (params) => (
        <div
          onClick={() => navigateToVistProfile(navigate, params.data.id)}
          style={{ cursor: 'pointer' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'email',
      headerName: 'E-mail',
      cellRenderer: (params) => {
        const { value } = params;
        const handleCellClick = () => {
          copyToClipboard(navigator, value);
        };
        return (
          <div onClick={handleCellClick} style={{ cursor: 'pointer' }}>
            {value}
          </div>
        );
      }
    },
    { field: 'role', headerName: 'Role', valueFormatter: ({ value }) => capitalize(value) },
    {
      field: 'created_at',
      headerName: 'Invited',
      valueFormatter: ({ value }) => moment(value).format('MM/DD/YYYY HH:mm.ss')
    },
    { field: 'color', headerName: 'Color', cellRenderer: ColorCircle },
    {
      field: 'actions',
      headerName: '',
      cellRenderer: (params) => <Actions index={params.rowIndex} data={params.data} />,
      maxWidth: 100,
      minWidth: 36,
      resizable: false
    }
  ];

  return (
    <Allowed requiredPermissions="member.read" showMessage showIllustration>
      <AgTablePage
        defaultColumns={cols}
        defaultFilters={DEFAULT_FILTERS}
        useData={useStaffPracticeAdmin}
        rightHeaderActions={HeaderActions}
        rowStyle={{ cursor: 'pointer' }}
        queryParams={{ practice_id: id }}
        name="members"
        title="Staff"
        keepOldFilters
      />
      <PracticeStaffModals
        deletePracticeStaffModal={deletePracticeStaffModal}
        setDeletePracticeStaffModal={setDeletePracticeStaffModal}
        selectedStaff={selectedStaff}
        setSelectedStaff={setSelectedStaff}
        memberToSupervisor={memberToSupervisor}
        show={show}
        setShow={setShow}
      />
    </Allowed>
  );
}
